import { takeLatest, all, call, put } from "redux-saga/effects";
import type { AxiosResponse } from "axios";
import type { ReduxAction } from "actions/ReduxActionTypes";
import {
  ReduxActionTypes,
  ReduxActionErrorTypes,
} from "ee/constants/ReduxActionConstants";
import { toast } from "@appsmith/ads";
import { validateResponse } from "sagas/ErrorSagas";
import SSHKeysApi from "ee/api/SSHKeysApi";
import type {
  FetchSSHKeysResponse,
  CreateSSHKeyResponse,
  DeleteSSHKeyResponse,
  ImportSSHKeyResponse,
  ShareSSHKeyResponse,
} from "ee/api/SSHKeysApi";
import type {
  SSHKeyApiError,
  CreateSSHKeyParams,
  ShareSSHKeyParams,
  RevokeSSHKeyParams,
} from "ee/types/sshKeysTypes";

function* fetchSSHKeysSaga() {
  let response: FetchSSHKeysResponse | undefined;

  try {
    response = yield call(SSHKeysApi.fetchSSHKeys);
    const isValidResponse: boolean = yield validateResponse(response);

    if (response && isValidResponse) {
      yield put({
        type: ReduxActionTypes.FETCH_SSH_KEYS_SUCCESS,
        payload: response.data,
      });
    }
  } catch (e) {
    const error = e as SSHKeyApiError;

    yield put({
      type: ReduxActionErrorTypes.FETCH_SSH_KEYS_ERROR,
      payload: {
        error: {
          ...error,
          message: error?.message ?? "Failed to fetch SSH keys",
        },
      },
    });
  }
}

function* createSSHKeySaga(action: ReduxAction<CreateSSHKeyParams>) {
  let response: CreateSSHKeyResponse | undefined;

  try {
    response = yield call(SSHKeysApi.createSSHKey, action.payload);
    const isValidResponse: boolean = yield validateResponse(response);

    if (response && isValidResponse) {
      yield put({
        type: ReduxActionTypes.CREATE_SSH_KEY_SUCCESS,
        payload: response.data,
      });
      toast.show("SSH key created successfully", { kind: "success" });
    }
  } catch (e) {
    const error = e as SSHKeyApiError;

    yield put({
      type: ReduxActionErrorTypes.CREATE_SSH_KEY_ERROR,
      payload: {
        error: {
          ...error,
          message: error?.message ?? "Failed to create SSH key",
        },
      },
    });
    toast.show(error?.message ?? "Failed to create SSH key", { kind: "error" });
  }
}

function* deleteSSHKeySaga(action: ReduxAction<{ id: string }>) {
  let response: DeleteSSHKeyResponse | undefined;

  try {
    response = yield call(SSHKeysApi.deleteSSHKey, action.payload.id);
    const isValidResponse: boolean = yield validateResponse(response);

    if (response && isValidResponse) {
      yield put({
        type: ReduxActionTypes.DELETE_SSH_KEY_SUCCESS,
        payload: { id: action.payload.id },
      });
      toast.show("SSH key deleted successfully", { kind: "success" });
    }
  } catch (e) {
    const error = e as SSHKeyApiError;

    yield put({
      type: ReduxActionErrorTypes.DELETE_SSH_KEY_ERROR,
      payload: {
        error: {
          ...error,
          message: error?.message ?? "Failed to delete SSH key",
        },
      },
    });
    toast.show(error?.message ?? "Failed to delete SSH key", { kind: "error" });
  }
}

function* importSSHKeySaga(action: ReduxAction<{ file: File }>) {
  let response: ImportSSHKeyResponse | undefined;

  try {
    response = yield call(SSHKeysApi.importSSHKey, action.payload.file);
    const isValidResponse: boolean = yield validateResponse(response);

    if (response && isValidResponse) {
      yield put({
        type: ReduxActionTypes.IMPORT_SSH_KEY_SUCCESS,
        payload: response.data,
      });
      toast.show("SSH key imported successfully", { kind: "success" });
    }
  } catch (e) {
    const error = e as SSHKeyApiError;

    yield put({
      type: ReduxActionErrorTypes.IMPORT_SSH_KEY_ERROR,
      payload: {
        error: {
          ...error,
          message: error?.message ?? "Failed to import SSH key",
        },
      },
    });
    toast.show(error?.message ?? "Failed to import SSH key", { kind: "error" });
  }
}

/**
 * Extract filename from Content-Disposition header.
 * Handles both standard and RFC 5987 formats.
 */
function extractFilename(contentDisposition: string | undefined): string {
  if (!contentDisposition) return "ssh-key-export.json";

  const rfc5987Match = contentDisposition.match(
    /filename\*=(?:UTF-8|utf-8)''([^;\s]+)/,
  );

  if (rfc5987Match?.[1]) {
    return decodeURIComponent(rfc5987Match[1]);
  }

  const standardMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);

  return standardMatch?.[1] || "ssh-key-export.json";
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function* exportSSHKeySaga(action: ReduxAction<{ id: string }>) {
  try {
    const response: AxiosResponse<Blob> = yield call(
      SSHKeysApi.exportSSHKey,
      action.payload.id,
    );

    const contentDisposition = response.headers?.["content-disposition"];
    const filename = extractFilename(contentDisposition);

    downloadBlob(response.data, filename);

    yield put({ type: ReduxActionTypes.EXPORT_SSH_KEY_SUCCESS });
  } catch (e) {
    const error = e as SSHKeyApiError;

    yield put({
      type: ReduxActionErrorTypes.EXPORT_SSH_KEY_ERROR,
      payload: {
        error: {
          ...error,
          message: error?.message ?? "Failed to export SSH key",
        },
      },
    });
  }
}

function* shareSSHKeySaga(action: ReduxAction<ShareSSHKeyParams>) {
  let response: ShareSSHKeyResponse | undefined;

  try {
    response = yield call(
      SSHKeysApi.shareSSHKey,
      action.payload.id,
      action.payload.emails,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (response && isValidResponse) {
      yield put({
        type: ReduxActionTypes.SHARE_SSH_KEY_SUCCESS,
        payload: response.data,
      });
      toast.show("SSH key shared successfully", { kind: "success" });
    }
  } catch (e) {
    const error = e as SSHKeyApiError;

    yield put({
      type: ReduxActionErrorTypes.SHARE_SSH_KEY_ERROR,
      payload: {
        error: {
          ...error,
          message: error?.message ?? "Failed to share SSH key",
        },
      },
    });
    toast.show(error?.message ?? "Failed to share SSH key", { kind: "error" });
  }
}

function* revokeSSHKeySaga(action: ReduxAction<RevokeSSHKeyParams>) {
  let response: ShareSSHKeyResponse | undefined;

  try {
    response = yield call(
      SSHKeysApi.revokeSSHKey,
      action.payload.id,
      action.payload.emails,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (response && isValidResponse) {
      yield put({
        type: ReduxActionTypes.REVOKE_SSH_KEY_SUCCESS,
        payload: response.data,
      });
      toast.show("Access revoked successfully", { kind: "success" });
    }
  } catch (e) {
    const error = e as SSHKeyApiError;

    yield put({
      type: ReduxActionErrorTypes.REVOKE_SSH_KEY_ERROR,
      payload: {
        error: {
          ...error,
          message: error?.message ?? "Failed to revoke access",
        },
      },
    });
    toast.show(error?.message ?? "Failed to revoke access", { kind: "error" });
  }
}

export default function* sshKeysSagas() {
  yield all([
    takeLatest(ReduxActionTypes.FETCH_SSH_KEYS_INIT, fetchSSHKeysSaga),
    takeLatest(ReduxActionTypes.CREATE_SSH_KEY_INIT, createSSHKeySaga),
    takeLatest(ReduxActionTypes.DELETE_SSH_KEY_INIT, deleteSSHKeySaga),
    takeLatest(ReduxActionTypes.EXPORT_SSH_KEY_INIT, exportSSHKeySaga),
    takeLatest(ReduxActionTypes.IMPORT_SSH_KEY_INIT, importSSHKeySaga),
    takeLatest(ReduxActionTypes.SHARE_SSH_KEY_INIT, shareSSHKeySaga),
    takeLatest(ReduxActionTypes.REVOKE_SSH_KEY_INIT, revokeSSHKeySaga),
  ]);
}
